import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../../shared/API/apiClient";
import { useReportingYear } from "../../../context/ReportingYearContext";
import Toast from "../../../shared/components/Toast";

const VALIDATIONS = {
  orgName: { required: true, label: "Organization Name" },
  cin: {
    required: true,
    label: "CIN",
    length: 21,
    hint: "Must be 21 characters (e.g. L12345MH2000PLC123456)",
  },
  pan: {
    required: true,
    label: "PAN",
    length: 10,
    hint: "Must be 10 characters (e.g. AABCA1234A)",
  },
  address: { required: true, label: "Registered Address" },
  industry: { required: true, label: "Industry" },
};

const EMPTY_FORM = {
  orgName: "",
  cin: "",
  pan: "",
  address: "",
  industry: "",
  industry_id: "",
  country: "",
  country_id: "",
  state: "",
  state_id: "",
  city: "",
  pincode: "",
};

const validate = (form) => {
  const errors = {};
  for (const [field, rules] of Object.entries(VALIDATIONS)) {
    const value = form[field]?.trim();
    if (rules.required && !value) {
      errors[field] = `${rules.label} is required`;
      continue;
    }
    if (value && rules.length && value.length !== rules.length) {
      errors[field] = `${rules.label} must be exactly ${rules.length} characters`;
      continue;
    }
    if (value && rules.pattern && !rules.pattern.test(value)) {
      errors[field] = rules.hint || `${rules.label} format is invalid`;
    }
  }
  return errors;
};

const Field = ({
  id,
  label,
  placeholder,
  form,
  errors,
  isLoading,
  isDisabled,
  onChange,
  onBlur,
  type = "text",
  options = [],
}) => (
  <div className="org-form-field">
    <label className="org-field-label">{label}</label>

    {type === "select" ? (
      <select
        className={`org-field-input ${errors[id] ? "org-field-input--error" : ""}`}
        value={form[`${id}_id`] || ""}
        onChange={onChange}
        disabled={isDisabled}
      >
        <option value="">{placeholder || "Select"}</option>
        {options.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.name || opt.state_name || opt.country_name || opt.industry_name}
          </option>
        ))}
      </select>
    ) : (
      <input
        className={`org-field-input ${errors[id] ? "org-field-input--error" : ""}`}
        type="text"
        placeholder={isLoading ? "Loading..." : placeholder}
        value={form[id] || ""}
        onChange={onChange(id)}
        onBlur={onBlur(id)}
        disabled={isDisabled}
      />
    )}

    {errors[id] && <span className="org-field-error">{errors[id]}</span>}
  </div>
);

const OrganizationProfile = () => {
  const navigate = useNavigate();
  const { reportingYear } = useReportingYear();
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [industries, setIndustries] = useState([]);

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const savedForm = useRef(EMPTY_FORM);
  const [toast, setToast] = useState(null);
  const showToast = (message, type = "success") => setToast({ message, type });

  // ── fetch existing profile on mount ───────────────────────────────────────
  useEffect(() => {
    const fetchProfile = async () => {
      setStatus("loading");
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        const org_id = user?.org_id;
        if (!org_id) { setStatus("idle"); return; }

        const response = await apiClient.get(`/api/organization-profiles?org_id=${org_id}`);
        const data = response.data;

        if (data.code === 200 && data.data?.length > 0) {
          const profile = data.data[0];
          const mapped = {
            orgName:     profile.organization_name ?? "",
            cin:         profile.cin ?? "",
            pan:         profile.pan ?? "",
            address:     profile.registered_address ?? "",
            industry:    profile.industry ?? "",
            industry_id: profile.industry_id ?? "",
            country:     profile.country ?? "",
            country_id:  profile.country_id ?? "",
            state:       profile.state ?? "",
            state_id:    profile.state_id ?? "",
            city:        profile.city ?? "",
            pincode:     profile.pincode ?? ""
          };
          savedForm.current = mapped;
          setForm(mapped);
        }
      } catch (err) {
        console.error("Failed to fetch organization profile:", err);
      } finally {
        setStatus("idle");
      }
    };
    fetchProfile();
  }, []);

  // ── helpers ───────────────────────────────────────────────────────────────
  const handleChange = (field) => (e) => {
    const value = field === "cin" || field === "pan"
      ? e.target.value.toUpperCase()
      : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    if (status !== "idle") setStatus("idle");
  };

  const handleBlur = (field) => () => {
    const fieldErrors = validate({ ...form });
    if (fieldErrors[field])
      setErrors((prev) => ({ ...prev, [field]: fieldErrors[field] }));
  };

  const handleCancel = () => {
    setForm(savedForm.current);
    setErrors({});
    setStatus("idle");
    setToast(null);
  };

  // ── dropdowns ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await apiClient.get(`/api/country-industries`);
        setCountries(res.data.countries);
        setIndustries(res.data.industries);
      } catch (err) {
        console.error("API load error", err);
      }
    };
    loadData();
  }, []);

  const handleCountryChange = async (e, isInit = false) => {
    const id = e.target.value;
    const selected = countries.find((c) => c.id == id);
    setForm((prev) => ({
      ...prev,
      country:  selected?.country_name || "",
      country_id: id,
      state:    isInit ? prev.state : "",
      state_id: isInit ? prev.state_id : "",
    }));
    try {
      const res = await apiClient.get(`/api/states?country_id=${id}`);
      setStates(res.data.data);
    } catch (err) {
      console.error("State API error", err);
    }
  };

  useEffect(() => {
    if (form.country_id && countries.length > 0) {
      handleCountryChange({ target: { value: form.country_id } }, true);
    }
  }, [form.country_id, countries]);

  const handleIndustryChange = (e) => {
    const id = e.target.value;
    const selected = industries.find((i) => i.id == id);
    setForm((prev) => ({
      ...prev,
      industry:    selected?.industry_name || "",
      industry_id: id,
    }));
  };

  const handleStateChange = (e) => {
    const id = e.target.value;
    const selected = states.find((s) => s.id == id);
    setForm((prev) => ({ ...prev, state_id: id, state: selected?.state_name || "" }));
  };

  // ── save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const fieldErrors = validate(form);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    setStatus("saving");
    try {
      const org_id = localStorage.getItem("org_id") ?? 2;
      const payload = {
        org_id:               Number(org_id),
        organization_name:    form.orgName.trim(),
        cin:                  form.cin.trim(),
        pan:                  form.pan.trim(),
        registered_address:   form.address.trim(),
        country_id:           Number(form.country_id),
        state_id:             Number(form.state_id),
        industry_id:          Number(form.industry_id),
        city:                 form.city.trim(),
        pincode:              form.pincode.trim(),
        reporting_year:        reportingYear,
        is_active:            true,
      };

      const response = await apiClient.post("/api/organization-profiles", payload);
      const data = response.data;

      if (data.code === 200) {
        savedForm.current = { ...form };
        setStatus("success");
        showToast(data.message || "Profile saved successfully.", "success");
      } else {
        throw new Error(data.message || "Update failed");
      }
    } catch (err) {
      setStatus("error");
      showToast(
        err?.response?.data?.message || err.message || "Something went wrong.",
        "error"
      );
    }
  };

  // ── render ────────────────────────────────────────────────────────────────
  const isLoading  = status === "loading";
  const isSaving   = status === "saving";
  const isDisabled = isLoading || isSaving;

  const fieldProps = { form, errors, isLoading, isDisabled, onChange: handleChange, onBlur: handleBlur };

  return (
    <div className="dash-page">

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="dash-page-title">Organization Profile</div>

      <div className="dash-page-subtitle">
        <span>Company-level information as required under BRSR Section A</span>
        {/* ✅ Navigates to the standalone page instead of opening a modal */}
        {/* <button
          className="org-btn-save le-add-btn"
          onClick={() => navigate("/manage_reporting_year")}
        >
          Manage Reporting Year
        </button> */}
      </div>

      {isLoading && (
        <div className="dash-loader-wrapper">
          <div className="dash-loader"></div>
          <p className="dash-loader-text">Fetching Profile</p>
        </div>
      )}

      <div>&nbsp;</div>

      <div className="org-form-card">

        {/* Row 1 */}
        <div className="org-form-row">
          <Field id="orgName" label={<>Organization Name <span className="required">*</span></>} placeholder="e.g. Acme Industries Limited" {...fieldProps} />
          <Field id="cin"     label={<>Corporate Identity Number (CIN) <span className="required">*</span></>} placeholder="e.g. L12345MH2000PLC123456" {...fieldProps} />
        </div>

        {/* Row 2 */}
        <div className="org-form-row">
          <Field id="pan"     label={<>PAN <span className="required">*</span></>} placeholder="e.g. AABCA1234A" {...fieldProps} />
          <Field id="address" label={<>Registered Address <span className="required">*</span></>} placeholder="e.g. 123 Business Park, Mumbai 400001" {...fieldProps} />
        </div>

        {/* Row 3 */}
        <div className="org-form-row">
          <Field id="industry" label="Industry" type="select" options={industries} form={form} errors={errors} isLoading={isLoading} isDisabled={isDisabled} onChange={handleIndustryChange} />
          <Field id="country"  label="Country"  type="select" options={countries}  form={form} errors={errors} isLoading={isLoading} isDisabled={isDisabled} onChange={handleCountryChange} />
        </div>

        {/* Row 4 */}
        <div className="org-form-row">
          <Field id="state" label="State" type="select" options={states} form={form} errors={errors} isLoading={isLoading} isDisabled={!form.country_id} onChange={handleStateChange} />
          <Field id="city"  label={<>City <span className="required">*</span></>} placeholder="Enter City" {...fieldProps} />
        </div>

        {/* Row 5 */}
        <div className="org-form-row">
          <Field id="pincode" label={<>Pincode <span className="required">*</span></>} placeholder="500001" {...fieldProps} />
          <div className="org-form-field" />
        </div>

        <div className="org-form-divider" />

        <div className="org-form-actions">
          <button className="org-btn-cancel" onClick={handleCancel} disabled={isDisabled}>Cancel</button>
          <button className="org-btn-save"   onClick={handleSave}   disabled={isDisabled}>
            {isSaving ? "Saving…" : "Save Profile"}
          </button>
        </div>

      </div>
    </div>
  );
};

export default OrganizationProfile;
